import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/api';

const ManageJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Load jobs on component mount
  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getMyJobs();
      
      const rawJobs = Array.isArray(response)
        ? response
        : (response.jobs || response.data?.jobs || response.data || []);
      
      if (Array.isArray(rawJobs)) {
        // Transform the data to match the DataGrid format
        const transformedJobs = rawJobs.map(job => ({
          id: job._id,
          title: job.title,
          type: job.jobType || 'Full-time',
          location: job.location ? `${job.location.city}, ${job.location.country}` : 'Not specified',
          status: job.status,
          applicants: job.applicationCount || 0,
          postedDate: new Date(job.createdAt).toLocaleDateString(),
          deadline: job.applicationDeadline ? new Date(job.applicationDeadline).toLocaleDateString() : 'No deadline',
        }));
        setJobs(transformedJobs);
      } else {
        setError('Failed to load jobs');
      }
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (job) => {
    setSelectedJob(job);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await ApiService.deleteJob(selectedJob.id);
      
      if (response.success) {
        // Remove the job from the local state
        setJobs(jobs.filter((job) => job.id !== selectedJob.id));
        setDeleteDialogOpen(false);
        setSelectedJob(null);
      } else {
        setError('Failed to delete job');
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      setError(err.message || 'Failed to delete job');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedJob(null);
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'active':
      case 'approved':
        return 'success';
      case 'paused':
      case 'expired':
        return 'warning';
      case 'closed':
      case 'rejected':
      case 'removed':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns = [
    { field: 'title', headerName: 'Job Title', flex: 1 },
    { field: 'type', headerName: 'Type', width: 130 },
    { field: 'location', headerName: 'Location', width: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'applicants',
      headerName: 'Applicants',
      type: 'number',
      width: 100,
    },
    { field: 'postedDate', headerName: 'Posted Date', width: 130 },
    { field: 'deadline', headerName: 'Deadline', width: 130 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Applicants">
            <IconButton
              onClick={() => navigate(`/employer/jobs/${params.row.id}/applicants`)}
              size="small"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Job">
            <IconButton
              onClick={() => navigate(`/employer/jobs/${params.row.id}/edit`)}
              size="small"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Job">
            <IconButton
              onClick={() => handleDeleteClick(params.row)}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Manage Jobs
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/employer/post-job')}
        >
          Post New Job
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={jobs}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          autoHeight
          disableSelectionOnClick
          sx={{
            backgroundColor: 'background.paper',
            boxShadow: 1,
            borderRadius: 1,
            p: 1,
          }}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Job Listing
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete the job listing for
          {selectedJob ? ` "${selectedJob.title}"` : ''}? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageJobs;